import { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  generateV2ServiceCategoryOptions,
  getAncestors,
  getDescendants,
} from "../../utils/serviceCategoryV2Mapping";
import { vendorTypeColors } from "../../utils/constants";
import { sortVendorTypes } from "../../utils/vendorTypeSort";
import {
  useGetAllIndications,
  useGetListForProviderTherapeutic,
  useGetprovidersListForFilters,
} from "../../custom-hooks/queries";
import { SearchDropdown } from "../SearchDropdown";
import {
  useComapnySizeData,
  useProviderTypes,
  useRfiListForFilters,
} from "../../custom-hooks/useFiltersData-hooks";
import LocationFiltersEditor from "../Filters/LocationFiltersEditor";
import { getLocationFromProvider } from "../../utils/addressUtils";
import useDiscoveryVendorFiltersSticky from "../../custom-hooks/useDiscoveryVendorFiltersSticky";

const revenueRangeOptions = [
  { value: "Under $500,000", label: "Under $500,000" },
  { value: "$500,000 - $1 mil.", label: "$500,000 - $1 mil." },
  { value: "$1 mil. - $5 mil.", label: "$1 mil. - $5 mil." },
  { value: "$5 mil. - $10 mil.", label: "$5 mil. - $10 mil." },
  { value: "$10mil. - $25 mil.", label: "$10mil. - $25 mil." },
  { value: "$25 mil. - $50 mil.", label: "$25 mil. - $50 mil." },
  { value: "$50 mil. - $100 mil.", label: "$50 mil. - $100 mil." },
  { value: "$100 mil. - $250 mil.", label: "$100 mil. - $250 mil." },
  { value: "$500 mil. - $1 bil.", label: "$500 mil. - $1 bil." },
  { value: "$1 bil. - $5 bil.", label: "$1 bil. - $5 bil." },
  { value: "Over $5 bil.", label: "Over $5 bil." },
];
const docAvailOptions = [
  { value: "rfi", label: "VQQ Available", dotColorClass: "bg-blue-600" },
  {
    value: "audit",
    label: "Audit Report Available",
    dotColorClass: "bg-red-500",
  },
];
/** A VQA only org is not offered a filter for vendors with VQQs it cannot use. */
export const documentAvailabilityOptions = (hideVqq) =>
  hideVqq ? docAvailOptions.filter((option) => option.value !== "rfi") : docAvailOptions;

const gxpOptions = [
  { value: "GAMP", label: "GAMP - Automated Manufacturing", indent: false },
  { value: "GCLP", label: "GCLP - Clinical Laboratory", indent: false },
  { value: "GCP", label: "GCP - Clinical", indent: false },
  { value: "GDP", label: "GDP - Distribution", indent: false },
  { value: "GLP", label: "GLP - Laboratory", indent: false },
  { value: "GMP", label: "GMP - Manufacturing", indent: false },
  { value: "GVP", label: "GVP - Pharmacovigilance", indent: false },
];

export const VendorFilters = ({
  generalSearchQuery = "",
  onFilterChange,
  defaultSelectedVendors = [],
  hideVqq = false,
}) => {
  const {
    selectedVendors,
    setSelectedVendors,
    selectedTAIndications,
    setSelectedTAIndications,
    selectedCategories,
    setSelectedCategories,
    selectedVendorType,
    setSelectedVendorType,
    selectedDataAvailability,
    setSelectedDataAvailability,
    selectedEmployeeSizes,
    setSelectedEmployeeSizes,
    selectedDocAvail,
    setSelectedDocAvail,
    selectedRevenueRanges,
    setSelectedRevenueRanges,
    locationFilters,
    setLocationFilters,
  } = useDiscoveryVendorFiltersSticky();
  // A VQA only org never filters on VQQs, even by a choice kept from before or from another page.
  const docAvailSelection = useMemo(
    () => (hideVqq ? selectedDocAvail.filter((option) => option.value !== "rfi") : selectedDocAvail),
    [hideVqq, selectedDocAvail]
  );

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [serviceColors, setServiceColors] = useState([]);
  const [isSCDropdownOpen, setIsSCDropdownOpen] = useState(false);
  const [scSearchQuery, setSCSearchQuery] = useState("");
  const scDropdownRef = useRef(null);
  const scSearchRef = useRef(null);

  const { data: therapeutic = [] } = useGetAllIndications();
  const { data: vendorsFilters } = useGetprovidersListForFilters({ hideDiscoveryStatus: true });
  const { data: therapeutics = [] } = useGetListForProviderTherapeutic();
  const { rfiListForFilters } = useRfiListForFilters();

  const v2SCOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfiListForFilters || []),
    [rfiListForFilters]
  );

  const filteredSCOptions = useMemo(() => {
    if (!scSearchQuery.trim()) return v2SCOptions;
    const q = scSearchQuery.toLowerCase();
    const matchingGroups = new Set();
    v2SCOptions.forEach((opt) => {
      if (!opt.isGroupHeader && opt.label.toLowerCase().includes(q)) {
        matchingGroups.add(opt.groupName);
      }
    });
    return v2SCOptions.filter((opt) => {
      if (opt.isGroupHeader) return matchingGroups.has(opt.label);
      return opt.label.toLowerCase().includes(q);
    });
  }, [v2SCOptions, scSearchQuery]);

  // Name→ID lookup for V2 options
  const scNameToId = useMemo(() => {
    const map = {};
    (v2SCOptions || []).forEach((o) => {
      if (!o.isGroupHeader && o.rfiId) map[o.value] = o.rfiId;
    });
    return map;
  }, [v2SCOptions]);

  // Helper: create selection object with rfi_id as value (matching original behavior)
  const makeSelection = (name) => ({
    value: scNameToId[name] || name,
    label: name,
  });

  // Dedupe by label so duplicate-named RFIs (e.g. "Wearables (All Types)")
  // never produce two badges or two selections
  const dedupeByLabel = (arr) => {
    const seen = new Set();
    const result = [];
    for (const item of arr || []) {
      const key = (item?.label ?? item?.value ?? "").toString().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  };
  const setSelectedCategoriesDedup = (next) =>
    setSelectedCategories(dedupeByLabel(next));

  const handleSCToggle = (value) => {
    const opt = v2SCOptions.find((o) => o.value === value);
    if (!opt) return;
    if (opt.isGroupHeader) {
      const members = opt.groupMembers || [];
      const memberIds = members.map((m) => scNameToId[m]).filter(Boolean);
      const allSelected = memberIds.every((id) =>
        selectedCategories.some((sc) => sc.value === id)
      );
      if (allSelected) {
        const memberIdSet = new Set(memberIds);
        setSelectedCategoriesDedup(
          selectedCategories.filter((sc) => !memberIdSet.has(sc.value))
        );
      } else {
        const existing = new Set(selectedCategories.map((sc) => sc.value));
        const toAdd = members
          .filter((m) => !existing.has(scNameToId[m]))
          .map((m) => makeSelection(m));
        setSelectedCategoriesDedup([...selectedCategories, ...toAdd]);
      }
      return;
    }
    const optId = scNameToId[value];
    const isSelected = selectedCategories.some((sc) => sc.value === optId);
    if (isSelected) {
      setSelectedCategoriesDedup(selectedCategories.filter((sc) => sc.value !== optId));
    } else {
      const namesToAdd = [value, ...getDescendants(value), ...getAncestors(value)];
      const existing = new Set(selectedCategories.map((sc) => sc.value));
      const newItems = namesToAdd
        .filter((n) => scNameToId[n] && !existing.has(scNameToId[n]) && v2SCOptions.some((o) => o.value === n))
        .map((n) => makeSelection(n));
      setSelectedCategoriesDedup([...selectedCategories, ...newItems]);
    }
  };

  const removeSCTag = (value) => {
    setSelectedCategories(selectedCategories.filter((sc) => sc.value !== value));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (scDropdownRef.current && !scDropdownRef.current.contains(e.target)) {
        setIsSCDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isSCDropdownOpen && scSearchRef.current) scSearchRef.current.focus();
  }, [isSCDropdownOpen]);

  const { providerTypesFilter } = useProviderTypes();
  const { companySizeData } = useComapnySizeData();

  const companySizeOptions =
    companySizeData?.map((data) => ({
      value: data.range,
      label: data.range,
    })) ?? [];

  const mappingRfisColor = () => {
    // Map `services` to match their colors from `rfis`
    const colors = rfiListForFilters?.reduce((prev, service) => {
      prev[service.rfi_name] = {
        rfi_id: service.rfi_id,
        bgColor: service.rfi_color_code_bg ?? "#e0e0e0",
        textColor: service.rfi_color_code_text ?? "#000",
        borderColor: service.rfi_color_code_border ?? "#ccc",
        name: service.rfi_name,
      };
      return prev;
    }, {});

    setServiceColors(colors || []);
  };

  useEffect(() => {
    mappingRfisColor();
  }, [rfiListForFilters]);

  useEffect(() => {
    if (defaultSelectedVendors?.length) {
      setSelectedVendors(defaultSelectedVendors);
    }
  }, [defaultSelectedVendors, setSelectedVendors]);

  useEffect(() => {
    onFilterChange?.({
      selectedVendors,
      selectedCategories,
      selectedVendorType,
      selectedTAIndications,
      selectedDocAvail: docAvailSelection,
      selectedDataAvailability,
      selectedEmployeeSizes,
      selectedRevenueRanges,
      locationFilters,
    });
  }, [
    selectedVendors,
    selectedCategories,
    selectedVendorType,
    selectedTAIndications,
    docAvailSelection,
    selectedDataAvailability,
    selectedEmployeeSizes,
    selectedRevenueRanges,
    locationFilters,
    onFilterChange,
  ]);

  const parseTherapeuticData = () => {
    const search = generalSearchQuery?.toLowerCase();
    const mapped = therapeutic
      ?.filter((data) =>
        !search
          ? true
          : data.therapeutic_area.toLowerCase().includes(search) ||
            data.indication.toLowerCase().includes(search)
      )
      ?.reduce((prev, data) => {
        prev[data.therapeutic_area] = prev[data.therapeutic_area]
          ? prev[data.therapeutic_area].concat({
              id: data.id,
              name: data.indication,
            })
          : [{ id: data.id, name: data.indication }];
        return prev;
      }, {});
    return Object.entries(mapped).map(([ta_name, indications]) => ({
      ta_name,
      ta_code: therapeutics.find((ta) => ta.therapeutic_area === ta_name)
        ?.therapeutic_area_id,
      indications,
    }));
  };

  const vendorOptions = [
    {
      value: "All Vendors",
      label: "All Vendors",
      dotColorClass: null,
      key: "all-vendors",
      indent: false,
    },
    ...(vendorsFilters && vendorsFilters.length > 0
      ? vendorsFilters?.map((vendor) => {
          return {
            value: vendor.id || vendor.value || vendor.name || vendor.label,
            label: vendor.label || vendor.name || vendor.value,
            dotColorClass: "bg-green-400",
            key: vendor.value || vendor.name || vendor.label,
            indent: true,
            visibility: vendor.visibility,
          };
        })
      : [
          {
            value: "no-vendors",
            label: "No vendors found",
            dotColorClass: null,
            key: "no-vendors",
            indent: true,
          },
        ]),
  ];

  const allServiceCategories = [
    ...(rfiListForFilters ?? [])?.map((cat) => {
      let dotColorClass = null;
      const color = serviceColors[cat.rfi_name];
      if (color) {
        dotColorClass = color.bgColor.replace(
          /-50|-100|-200|-300|-400/g,
          "-500"
        );
      }
      return {
        value: cat.rfi_id,
        label: cat.rfi_name,
        dotColorClass: dotColorClass ?? cat.rfi_color_code_bg,
        key: cat.rfi_id,
        indent: true,
      };
    }),
  ];

  const allVendorTypes = [
    {
      value: "All Types",
      label: "All Types",
      dotColorClass: null,
      key: "all-types",
      indent: false,
    },
    ...sortVendorTypes(providerTypesFilter)
      .map((type) => {
        let dotColorClass = vendorTypeColors[type.name] || "bg-gray-400";
        return { value: type.id, label: type.name, dotColorClass, indent: true };
      }),
  ];
  const dataAvailabilityOptions = [
    ...(gxpOptions || [])?.map((opt) => {
      let dotColorClass = opt?.dotColorClass;
      return {
        ...opt,
        dotColorClass,
        indent: true,
        count: "",
      };
    }),
  ];

  const getAllSelectedFilters = (
    selectedProviders = [],
    selectedCategories = [],
    selectedVendorType = [],
    selectedVendorVisibility = [],
    selectedTAIndications = {}
  ) => {
    const filters = [];
    const safeVendorOptions = Array.isArray(vendorOptions) ? vendorOptions : [];
    const safeAllServiceCategories = Array.isArray(allServiceCategories)
      ? allServiceCategories
      : [];
    const safeAllVendorTypes = Array.isArray(allVendorTypes)
      ? allVendorTypes
      : [];
    const safeDataAvailabilityOptions = Array.isArray(dataAvailabilityOptions)
      ? dataAvailabilityOptions
      : [];
    const therapeuticData = parseTherapeuticData();
    const safeTherapeuticData = Array.isArray(therapeuticData)
      ? therapeuticData
      : [];
    const hasAll = (options, allOptions) => {
      if (!options || !allOptions) return false;
      return options.length > allOptions.length / 2;
    };
    const allProviderOptions = safeVendorOptions.filter(
      (opt) => opt.value !== "All Vendors"
    );
    if (
      selectedProviders.length === allProviderOptions.length &&
      allProviderOptions.length > 0
    ) {
      filters.push({ type: "provider", value: "all", label: "All Vendors" });
    } else if (
      hasAll(selectedProviders, allProviderOptions) &&
      allProviderOptions.length > 0
    ) {
      const excluded = allProviderOptions.filter(
        (opt) => !selectedProviders.some((sel) => sel.value === opt.value)
      );
      filters.push({
        type: "provider",
        value: "all",
        label: `All Vendors: -${excluded.map((opt) => opt.label).join(", -")}`,
      });
    } else {
      filters.push(
        ...(selectedProviders || []).map((p) => ({
          type: "provider",
          value: p.value,
          label: p.label,
          visibility: p.visibility,
          dotColorClass: "bg-green-400",
        }))
      );
    }

    const allCategoryOptions = safeAllServiceCategories.filter(
      (opt) => !opt.value?.includes("All")
    );
    if (
      selectedCategories.length === allCategoryOptions.length &&
      allCategoryOptions.length > 0
    ) {
      filters.push({ type: "category", value: "all", label: "All Services" });
    } else if (
      hasAll(selectedCategories, allCategoryOptions) &&
      allCategoryOptions.length > 0
    ) {
      const excluded = allCategoryOptions.filter(
        (opt) => !selectedCategories.some((sel) => sel.value === opt.value)
      );
      filters.push({
        type: "category",
        value: "all",
        label: `All Services: -${excluded.map((opt) => opt.label).join(", -")}`,
      });
    } else {
      filters.push(
        ...(selectedCategories || []).map((c) => ({
          type: "category",
          value: c.value,
          label: c.label,
          colors: serviceColors[c.label],
        }))
      );
    }
    const allVendorTypeOptions = safeAllVendorTypes.filter(
      (opt) => !opt.value?.includes("All")
    );
    if (
      selectedVendorType.length === allVendorTypeOptions.length &&
      allVendorTypeOptions.length > 0
    ) {
      filters.push({ type: "vendorType", value: "all", label: "All Types" });
    } else if (
      hasAll(selectedVendorType, allVendorTypeOptions) &&
      allVendorTypeOptions.length > 0
    ) {
      const excluded = allVendorTypeOptions.filter(
        (opt) => !selectedVendorType.some((sel) => sel.value === opt.value)
      );
      filters.push({
        type: "vendorType",
        value: "all",
        label: `All Types: -${excluded.map((opt) => opt.label).join(", -")}`,
      });
    } else {
      filters.push(
        ...(selectedVendorType || []).map((t) => ({
          type: "vendorType",
          value: t.value,
          label: t.label,
          dotColorClass: t.dotColorClass,
        }))
      );
    }
    const allDataOptions = safeDataAvailabilityOptions.filter(
      (opt) => !opt.value?.includes("All")
    );
    if (
      selectedVendorVisibility.length === 1 &&
      selectedVendorVisibility[0].value === "data"
    ) {
      filters.push({
        type: "visibility",
        value: "data",
        label: "Any Data Available",
        dotColorClass: "",
      });
    } else if (selectedVendorVisibility.length > 0) {
      filters.push(
        ...selectedVendorVisibility
          .filter((v) => v.value !== "data")
          .map((v) => ({
            type: "gxp",
            value: v.value,
            label: v.value,
            dotColorClass: "",
            colors: {
              bgColor: "bg-slate-500",
              textColor: "text-white",
              borderColor: "ring-slate-500",
            },
          }))
      );
    }
    const allTAOptions = safeTherapeuticData?.map((t) => t.therapeuticArea);
    const selectedTAs = Object.keys(selectedTAIndications || {});
    if (selectedTAs.length === allTAOptions.length && allTAOptions.length > 0) {
      filters.push({
        type: "therapeutic-area",
        value: "all-ta",
        label: "All Therapeutic Areas & Indications",
      });
    } else if (hasAll(selectedTAs, allTAOptions) && allTAOptions.length > 0) {
      const excluded = allTAOptions.filter((ta) => !selectedTAs.includes(ta));
      filters.push({
        type: "therapeutic-area",
        value: "all-ta",
        label: `All Therapeutic Areas & Indications: -${excluded.join(", -")}`,
      });
    } else if (selectedTAs.length > 0) {
      Object.entries(selectedTAIndications).forEach(
        ([therapeuticArea, data]) => {
          const allIndicationsForTA =
            safeTherapeuticData.find((t) => t.ta_name === therapeuticArea)
              ?.indications || [];
          const label =
            data?.indications.length === 0
              ? therapeuticArea
              : data?.indications.length === allIndicationsForTA.length
              ? `${therapeuticArea}: All`
              : `${therapeuticArea}: ${data?.indications
                  ?.map((ind) => ind.name)
                  ?.join(", ")}`;
          filters.push({
            type: "therapeutic-area",
            value: therapeuticArea,
            label,
          });
        }
      );
    }
    return filters;
  };

  const filterOptionsByGeneralSearch = (options) => {
    if (!generalSearchQuery) return options;
    const searchLower = generalSearchQuery.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label?.toLowerCase().includes(searchLower) ||
        opt.value?.toLowerCase().includes(searchLower)
    );
  };

  return (
    <div className="bg-white border rounded-xl shadow-sm">
      {/* Header */}
      <div className="px-4 pt-4 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">
          Vendor Filters
        </h2>
        <p className="mt-1 mb-4 text-sm text-gray-500">
          Select vendors and categories to compare.
        </p>
      </div>
      <div className="p-4 space-y-4">
        {/* First row of filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchDropdown
            options={filterOptionsByGeneralSearch(vendorOptions)}
            selectedOptions={selectedVendors}
            setSelectedOptions={setSelectedVendors}
            placeholder="Vendors"
            serviceColors={serviceColors}
          />

          {/* V2 Service Categories dropdown */}
          <div className="relative" ref={scDropdownRef}>
            <div
              className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
              onClick={() => setIsSCDropdownOpen(!isSCDropdownOpen)}
            >
              <div className="flex flex-wrap items-center gap-1 min-h-[24px]">
                {selectedCategories.length > 0 ? (
                  dedupeByLabel(selectedCategories).map((tag) => {
                    const tagValue = tag.value || tag;
                    const tagLabel = tag.label || tag.value || tag;
                    const rfiItem = (rfiListForFilters ?? []).find((r) => r.rfi_id === tagValue || r.rfi_name === tagValue);
                    const bgColor = rfiItem?.rfi_color_code_bg || "#EEF2FF";
                    const textColor = rfiItem?.rfi_color_code_text || "#4338CA";
                    const borderColor = rfiItem?.rfi_color_code_border || "#4338CA";
                    return (
                      <span
                        key={tagValue}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                        style={{ backgroundColor: bgColor, color: textColor, '--tw-ring-color': borderColor + '33' }}
                      >
                        {tagLabel}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeSCTag(tagValue); }}
                          className="ml-0.5 inline-flex items-center hover:opacity-70"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })
                ) : (
                  <span className="text-sm text-gray-400">Service Categories</span>
                )}
              </div>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </div>

            {isSCDropdownOpen && (
              <div className="absolute z-[100] mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={scSearchRef}
                      type="text"
                      className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:outline-none"
                      placeholder="Search categories..."
                      value={scSearchQuery}
                      onChange={(e) => setSCSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <div className="max-h-[26rem] overflow-y-auto py-1 px-3" style={{ paddingBottom: "1rem" }}>
                  <ul className="space-y-0">
                    {filteredSCOptions.map((option, idx) => {
                      if (option.isGroupHeader) {
                        const members = option.groupMembers || [];
                        const selectedInGroup = members.filter((m) =>
                          selectedCategories.some((sc) => sc.value === m || sc.label === m)
                        );
                        const allSelected = members.length > 0 && selectedInGroup.length === members.length;
                        const someSelected = selectedInGroup.length > 0 && !allSelected;
                        return (
                          <li key={`grp-${idx}`} className="pt-3 pb-1 pl-3 first:pt-0 select-none">
                            <span
                              className={`text-[11px] font-semibold uppercase tracking-wider cursor-pointer transition-colors ${allSelected || someSelected ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                              onClick={(e) => { e.stopPropagation(); handleSCToggle(option.value); }}
                            >
                              {option.label}
                            </span>
                          </li>
                        );
                      }
                      const indentPx = (option.depth || 0) * 16;
                      const isChecked = selectedCategories.some(
                        (sc) => sc.value === option.rfiId
                      );
                      const rfiItem = (rfiListForFilters ?? []).find((r) => r.rfi_name === option.value);
                      const dotColor = rfiItem?.rfi_color_code_text || null;
                      return (
                        <li
                          key={`sc-${idx}`}
                          className={`relative select-none py-1 pr-9 cursor-pointer ${isChecked ? "bg-indigo-50" : "hover:bg-indigo-50"}`}
                          style={{ paddingLeft: `${12 + indentPx}px` }}
                          onClick={(e) => { e.stopPropagation(); handleSCToggle(option.value); }}
                        >
                          <div className="flex items-center gap-2">
                            {dotColor && (
                              <span
                                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: dotColor }}
                              />
                            )}
                            <span className={`text-sm ${isChecked ? "font-semibold" : "font-normal"} text-gray-900`}>
                              {option.label}
                            </span>
                          </div>
                          {isChecked && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                              <svg className="h-5 w-5 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center pb-4">
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="inline-flex items-center gap-x-1 text-sm font-medium text-gray-500 hover:text-indigo-600"
          >
            {showAdvancedFilters
              ? "Hide Advanced Filters"
              : "View Advanced Filters"}
            <svg
              className={`h-4 w-4 transform transition-transform duration-200 ${
                showAdvancedFilters ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {showAdvancedFilters && (
          <div className="space-y-4">
            {/* First row: Vendor Type & Therapeutic Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vendor type Dropdown */}
              <SearchDropdown
                options={filterOptionsByGeneralSearch(allVendorTypes)}
                selectedOptions={selectedVendorType}
                setSelectedOptions={setSelectedVendorType}
                placeholder="Vendor Type"
                serviceColors={serviceColors}
              />
              {/* TA indications Dropdown */}
              <SearchDropdown
                options={parseTherapeuticData()}
                selectedOptions={selectedTAIndications}
                setSelectedOptions={setSelectedTAIndications}
                placeholder="Therapeutic Areas & Indications"
              />
            </div>
            {/*TODO: initial filters layout with gxp input */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchDropdown
                options={filterOptionsByGeneralSearch(docAvailOptions)}
                selectedOptions={selectedDocAvail}
                setSelectedOptions={setSelectedDocAvail}
                placeholder="Document Availability"
              />
              <SearchDropdown
                options={filterOptionsByGeneralSearch(dataAvailabilityOptions)}
                selectedOptions={selectedDataAvailability}
                setSelectedOptions={setSelectedDataAvailability}
                placeholder="GxP"
                serviceColors={serviceColors}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchDropdown
                options={filterOptionsByGeneralSearch(companySizeOptions)}
                selectedOptions={selectedEmployeeSizes}
                setSelectedOptions={setSelectedEmployeeSizes}
                placeholder="Employees"
              />
              <SearchDropdown
                options={filterOptionsByGeneralSearch(revenueRangeOptions)}
                selectedOptions={selectedRevenueRanges}
                setSelectedOptions={setSelectedRevenueRanges}
                placeholder="Recent Annual Revenue"
              />
            </div> */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchDropdown
                options={filterOptionsByGeneralSearch(documentAvailabilityOptions(hideVqq))}
                selectedOptions={docAvailSelection}
                setSelectedOptions={setSelectedDocAvail}
                placeholder="Document Availability"
              />
              <SearchDropdown
                options={filterOptionsByGeneralSearch(companySizeOptions)}
                selectedOptions={selectedEmployeeSizes}
                setSelectedOptions={setSelectedEmployeeSizes}
                placeholder="Employees"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchDropdown
                options={filterOptionsByGeneralSearch(revenueRangeOptions)}
                selectedOptions={selectedRevenueRanges}
                setSelectedOptions={setSelectedRevenueRanges}
                placeholder="Recent Annual Revenue"
              />
            </div>
            {/* Location Filters Row */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-500">Location:</h3>
              <LocationFiltersEditor
                locationFilters={locationFilters}
                setLocationFilters={setLocationFilters}
                filterOptions={filterOptionsByGeneralSearch}
              />
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 px-4 pt-4 pb-4 mt-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-medium text-gray-500">Filters:</h3>
          <div className="flex flex-wrap gap-2">
            {getAllSelectedFilters(
              selectedVendors,
              selectedCategories,
              selectedVendorType,
              selectedDataAvailability,
              selectedTAIndications
            ).map((filter) => {
              const color = serviceColors[filter.label];
              const colorStyle = color
                ? {
                    backgroundColor: color.bgColor,
                    color: color.textColor,
                    boxShadow: `inset 0 0 0 1px ${color.borderColor}1A`,
                  }
                : filter?.colors?.bgColor?.startsWith("#")
                ? {
                    backgroundColor: filter.colors.bgColor,
                    color: filter.colors.textColor,
                    boxShadow: `inset 0 0 0 1px ${filter.colors.borderColor}1A`,
                  }
                : {};
              return (
                <span
                  key={filter.value}
                  style={colorStyle}
                   className={`inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ${
                    filter.type === "gxp"
                      ? "bg-slate-500 text-white"
                      : filter.colors
                      ? `${filter.colors.bgColor} ${filter.colors.textColor} border-[${filter.colors.borderColor}] ring-inset`
                      : "bg-white text-gray-900 ring-gray-200 ring-inset border"
                  }`}
                >
                  {filter.type === "provider" &&
                    !(filter.value || "").includes("All Vendors") &&
                    filter.dotColorClass && (
                      <span
                        className={`inline-block h-2 w-2 shrink-0 rounded-full ${filter.dotColorClass}`}
                        aria-hidden="true"
                      />
                    )}
                  {filter.type === "vendorType" &&
                    filter.dotColorClass &&
                    !(filter.value || "").includes("All Vendors") && (
                      <span
                        className={`inline-block h-2 w-2 shrink-0 rounded-full ${filter.dotColorClass}`}
                        aria-hidden="true"
                      />
                    )}
                  {filter.type === "visibility" &&
                    filter.dotColorClass &&
                    filter.value !== "Any Visibility" &&
                    !(filter.value || "").includes("All Vendors") && (
                      <span
                        className={`inline-block h-2 w-2 shrink-0 rounded-full ${filter.dotColorClass}`}
                        aria-hidden="true"
                      />
                    )}
                  {filter.label}
                  <button
                    type="button"
                    className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (filter.type === "provider") {
                        if (filter.value === "all") {
                          setSelectedVendors([]);
                        } else {
                          setSelectedVendors((prev) =>
                            prev.filter(
                              (p) =>
                                p.value !== filter.value &&
                                p.name !== filter.value &&
                                p.label !== filter.value &&
                                p.value !== filter.label &&
                                p.name !== filter.label &&
                                p.label !== filter.label
                            )
                          );
                        }
                      } else if (filter.type === "category") {
                        if (filter.value === "all") {
                          setSelectedCategories([]);
                        } else {
                          setSelectedCategories((prev) =>
                            prev.filter(
                              (c) =>
                                c.value !== filter.value &&
                                c.label !== filter.value
                            )
                          );
                        }
                      } else if (filter.type === "vendorType") {
                        if (filter.value === "all") {
                          setSelectedVendorType([]);
                        } else {
                          setSelectedVendorType((prev) =>
                            prev.filter(
                              (t) =>
                                t.value !== filter.value &&
                                t.label !== filter.value
                            )
                          );
                        }
                      } else if (filter.type === "gxp") {
                        if (filter.value === "data") {
                          setSelectedDataAvailability([]);
                        } else {
                          setSelectedDataAvailability((prev) =>
                            prev.filter(
                              (v) =>
                                v.value !== filter.value &&
                                v.label !== filter.value
                            )
                          );
                        }
                      } else if (filter.type === "therapeutic-area") {
                        if (filter.value === "all-ta") {
                          setSelectedTAIndications({});
                        } else {
                          setSelectedTAIndications((prev) => {
                            const newState = { ...prev };
                            delete newState[filter.value];
                            return newState;
                          });
                        }
                      }
                    }}
                  >
                    <span className="sr-only">Remove {filter.label}</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="h-3.5 w-3.5 stroke-current"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {/* Employee Size badges */}
            {selectedEmployeeSizes.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 bg-white text-gray-900 ring-gray-200 ring-inset"
              >
                {option.label}
                <button
                  type="button"
                  className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEmployeeSizes((prev) =>
                      prev.filter((o) => o.value !== option.value)
                    );
                  }}
                >
                  <span className="sr-only">Remove {option.label}</span>
                  <svg
                    viewBox="0 0 14 14"
                    className="h-3.5 w-3.5 stroke-current"
                  >
                    <path d="M4 4l6 6m0-6l-6 6" />
                  </svg>
                </button>
              </span>
            ))}
            {/* Revenue Range badges */}
            {selectedRevenueRanges.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 bg-white text-gray-900 ring-gray-200 ring-inset"
              >
                {option.label}
                <button
                  type="button"
                  className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRevenueRanges((prev) =>
                      prev.filter((o) => o.value !== option.value)
                    );
                  }}
                >
                  <span className="sr-only">Remove {option.label}</span>
                  <svg
                    viewBox="0 0 14 14"
                    className="h-3.5 w-3.5 stroke-current"
                  >
                    <path d="M4 4l6 6m0-6l-6 6" />
                  </svg>
                </button>
              </span>
            ))}
            {/* Location badge - combine city, state, country */}
            {locationFilters.map((filter, index) => {
              const hasValues = filter.city || filter.state || filter.country;
              if (!hasValues) return null;

              return (
                <span
                  key={filter.id}
                  className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium leading-none ring-1 bg-white text-gray-900 ring-gray-200 ring-inset"
                >
                  {getLocationFromProvider({ city: filter.city, state: filter.state?.value, country: filter.country?.value }, { short: false, abbvState: true })}
                  <button
                    type="button"
                    className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Clear this specific location filter
                      setLocationFilters((prev) =>
                        prev.map((f, i) =>
                          i === index
                            ? { ...f, country: null, city: "", state: null }
                            : f
                        )
                      );
                    }}
                  >
                    <span className="sr-only">Remove location filter</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="h-3.5 w-3.5 stroke-current"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                  </button>
                </span>
              );
            })}
            {/* Cosmetic Document Availability badges */}
            {docAvailSelection.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 bg-white text-gray-900 ring-gray-200 ring-inset"
              >
                {option.dotColorClass && (
                  <span
                    className={`inline-block h-2 w-2 shrink-0 rounded-full ${option.dotColorClass}`}
                    aria-hidden="true"
                  />
                )}
                {option.label}
                <button
                  type="button"
                  className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDocAvail((prev) =>
                      prev.filter((o) => o.value !== option.value)
                    );
                  }}
                >
                  <span className="sr-only">Remove {option.label}</span>
                  <svg
                    viewBox="0 0 14 14"
                    className="h-3.5 w-3.5 stroke-current"
                  >
                    <path d="M4 4l6 6m0-6l-6 6" />
                  </svg>
                </button>
              </span>
            ))}
            {/* End cosmetic badges */}
            {(getAllSelectedFilters(
              selectedVendors,
              selectedCategories,
              selectedVendorType,
              selectedDataAvailability,
              selectedTAIndications
            ).length > 0 ||
              selectedEmployeeSizes.length > 0 ||
              selectedRevenueRanges.length > 0 ||
              docAvailSelection.length > 0 ||
              locationFilters.some((f) => f.country || f.city || f.state)) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedVendors([]);
                  setSelectedCategories([]);
                  setSelectedVendorType([]);
                  setSelectedDataAvailability([]);
                  setSelectedTAIndications({});
                  setSelectedEmployeeSizes([]);
                  setSelectedRevenueRanges([]);
                  setSelectedDocAvail([]);
                  setLocationFilters([
                    { id: 1, country: null, city: "", state: null },
                  ]);
                }}
                className="m-1 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
